import { useFileRecordExport } from "@/panes/file-record-detail/FileRecordExportContext";
import { FileRecordSelectionActionOverlay } from "@/panes/file-record-detail/FileRecordSelectionActionOverlay";
import { useDomTextSelectionAction } from "@/panes/file-record-detail/useDomTextSelectionAction";
import { usePushFileRecordTextSelectionToChat } from "@/panes/file-record-detail/usePushFileRecordTextSelectionToChat";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { useEffect, useRef, useState } from "react";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

/**
 * Covers text/plain, text/csv, text/html (raw source), application/json,
 * application/xml, text/x-rst — anything where a monospace dump is the
 * honest rendering. Markdown has its own `MarkdownContent` renderer.
 */
export function TextContent({ record }: { record: FileRecord }) {
  const { setTextSource } = useFileRecordExport();
  const { previewFetchUrl, isPreviewing } = useFileRecordVersionPreview();
  const [text, setText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { enabled: textSelectionEnabled, pushSelection } =
    usePushFileRecordTextSelectionToChat({
      fileRecordId: record.id,
      fileRecordName: getFileRecordName(record),
    });
  const canReferenceSelectionInChat = textSelectionEnabled && !isPreviewing;

  const { selectedText, anchorRect, clearSelection } =
    useDomTextSelectionAction({
      enabled: canReferenceSelectionInChat && text != null,
      rootRef: contentRef,
    });

  const fetchUrl = previewFetchUrl ?? record.url;

  useEffect(() => {
    if (!fetchUrl) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void fetch(fetchUrl)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(`Failed (${res.status})`);
        const body = await res.text();
        if (!cancelled) setText(body);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchUrl]);

  useEffect(() => {
    setTextSource(text);
    return () => setTextSource(null);
  }, [text, setTextSource]);

  if (!fetchUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        File not available.
      </div>
    );
  }

  return (
    <div ref={contentRef} className="relative h-full w-full overflow-auto">
      <pre
        className={cn(
          "whitespace-pre-wrap break-words font-mono text-sm text-foreground p-4 transition-opacity duration-300",
          !isLoading && text != null ? "opacity-100" : "opacity-0",
        )}
      >
        {text}
      </pre>
      {isLoading && <LoadingOverlay />}
      {error && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {error}
        </div>
      )}
      {canReferenceSelectionInChat && (
        <FileRecordSelectionActionOverlay
          selectedText={selectedText}
          anchorRect={anchorRect}
          onActivate={pushSelection}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}
